// src/pages/Telegraph.jsx
import React, { useEffect } from 'react';
import './Telegraph.css';

export default function Telegraph() {
  // Load Google Fonts once
  useEffect(() => {
    const id = 'telegraph-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Inter:wght@300;400;500;600&display=swap';
    document.head.appendChild(link);
  }, []);

  // Helper to generate coil ellipses for the animated wire diagram
  const makeCoils = () => {
    const positions = [125, 140, 155, 170, 185, 200, 215, 230, 245, 260, 275, 290];
    const layers = [
      { rx: 6, ry: 18, delayBase: 1.2 },
      { rx: 7, ry: 21, delayBase: 3.0, reverse: true },
      { rx: 8, ry: 24, delayBase: 4.8 },
    ];
    const out = [];
    layers.forEach((layer, li) => {
      const order = layer.reverse ? [...positions].reverse() : positions;
      order.forEach((cx, i) => {
        out.push(
          <ellipse
            key={`l${li}-${cx}`}
            cx={cx}
            cy={180}
            rx={layer.rx}
            ry={layer.ry}
            className="ink-line coil-line"
            strokeWidth="1.2"
            style={{ animationDelay: `${layer.delayBase + i * 0.15}s` }}
          />
        );
      });
    });
    return out;
  };

  const morseAlphabet = [
    ['A', '·−'], ['B', '−···'], ['C', '−·−·'], ['D', '−··'], ['E', '·'],
    ['F', '··−·'], ['G', '−−·'], ['H', '····'], ['I', '··'], ['J', '·−−−'],
    ['K', '−·−'], ['L', '·−··'], ['M', '−−'], ['N', '−·'], ['O', '−−−'],
    ['P', '·−−·'], ['Q', '−−·−'], ['R', '·−·'], ['S', '···'], ['T', '−'],
    ['U', '··−'], ['V', '···−'], ['W', '·−−'], ['X', '−··−'], ['Y', '−·−−'],
    ['Z', '−−··'],
    ['0', '−−−−−'], ['1', '·−−−−'], ['2', '··−−−'], ['3', '···−−'],
    ['4', '····−'], ['5', '·····'], ['6', '−····'], ['7', '−−···'],
    ['8', '−−−··'], ['9', '−−−−·'],
  ];

  return (
    <div className="telegraph-page">

      {/* ====== TOP NAV ====== */}
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <img className="brand-mark" src="/logo flower only_small.png" alt="King's Academy" />
            <div className="brand-text">
              <span className="brand-name">King's Academy</span>
              <span className="brand-tag">Middle School Science · Grade 8</span>
            </div>
          </div>
          <nav className="nav-links">
            <a href="#overview"><span className="section-num">1</span>Overview</a>
            <a href="#electromagnet"><span className="section-num">2</span>Theory</a>
            <a href="#station-a"><span className="section-num">3</span>Station A</a>
            <a href="#station-b"><span className="section-num">4</span>Station B</a>
            <a href="#wiring"><span className="section-num">5</span>Wiring</a>
            <a href="#morse"><span className="section-num">6</span>Morse</a>
          </nav>
        </div>
      </header>

      {/* ====== MASTHEAD ====== */}
      <div className="masthead">
        <img className="masthead-ornament" src="/ka-logo.png" alt="King's Academy" />
        <div className="eyebrow">An 8th-grade engineering project</div>
        <h1>Build a Working <em>Telegraph</em></h1>
        <p className="masthead-sub">
          Wood, wire, iron, and a battery — the same ingredients Samuel Morse used in 1844.
          By the end of class, your group will tap out messages across the room.
        </p>
        <svg className="rule-ornate" viewBox="0 0 240 16" aria-hidden="true">
          <line x1="0" y1="8" x2="100" y2="8" stroke="#A29164" strokeWidth="0.5" />
          <line x1="140" y1="8" x2="240" y2="8" stroke="#A29164" strokeWidth="0.5" />
          <circle cx="120" cy="8" r="3" fill="#E83530" />
          <circle cx="108" cy="8" r="1" fill="#A29164" />
          <circle cx="132" cy="8" r="1" fill="#A29164" />
        </svg>
      </div>

      {/* ====== CONTENT ====== */}
      <main className="content">

        {/* SECTION 1: OVERVIEW */}
        <section id="overview">
          <div className="section-eyebrow">Section One</div>
          <div className="section-head">
            <span className="section-number">1.0</span>
            <h2>Overview &amp; Teams</h2>
          </div>

          <p className="lead">You have two hours, four people, and a pile of parts. By the end, you should be sending Morse code messages with audible clicks and a flashing light.</p>

          <p className="drop-cap">A telegraph turns a finger tap into a signal that can travel down a wire. Press a switch, and an electromagnet on the other end pulls a piece of metal down with a sharp click. Long press, long click — a dash. Short press, short click — a dot. Strings of dots and dashes spell letters. Strings of letters spell words. This is the technology that connected continents in the 1800s, and you are about to build a working version from scrap parts.</p>

          <h3 className="subhead"><span className="subhead-num">1.1</span>How the time breaks down</h3>

          <div className="timeline">
            {[
              ['0:00 – 0:10', 'Intro, safety briefing, team roles assigned'],
              ['0:10 – 0:45', 'Parallel build at Station A and Station B'],
              ['0:45 – 1:00', 'Mount everything to the wood base together'],
              ['1:00 – 1:25', 'Wire the full circuit, troubleshoot'],
              ['1:25 – 1:50', 'Learn Morse, send messages'],
              ['1:50 – 2:00', 'Cleanup, reflection, pack up'],
            ].map(([time, task]) => (
              <div className="timeline-block" key={time}>
                <div className="timeline-time">{time}</div>
                <div className="timeline-task">{task}</div>
              </div>
            ))}
          </div>

          <h3 className="subhead"><span className="subhead-num">1.2</span>Split into two pairs</h3>

          <p>For the first 35 minutes, your group of four splits into two pairs working in parallel. This is the only way to fit everything into the time you have.</p>

          <div className="station-banner">
            <div className="station-letter">A</div>
            <div>
              <h3>Electromagnet &amp; Sounder</h3>
              <p>Two students · Builds the magnet that makes the click</p>
            </div>
          </div>

          <div className="station-banner">
            <div className="station-letter">B</div>
            <div>
              <h3>Power Supply &amp; Key</h3>
              <p>Two students · Builds the battery mount and the tapper</p>
            </div>
          </div>

          <h3 className="subhead"><span className="subhead-num">1.3</span>Your toolbox</h3>

          <p>Each group gets the same kit. Lay it out on your bench before you start so you can see what you have:</p>

          <ul className="checklist">
            <li>One 2x4 wood base</li>
            <li>One long steel nail</li>
            <li>Roughly 3 metres of 20-gauge wire</li>
            <li>Two D-cell batteries</li>
            <li>Two small metal angle brackets</li>
            <li>One small spring (for battery contact)</li>
            <li>One hacksaw blade (you will cut it in two)</li>
            <li>Strips of metal tape</li>
            <li>Small light bulb and matching socket</li>
            <li>Several small alligator clips with leads</li>
            <li>Wood screws (assorted)</li>
            <li>Sandpaper</li>
            <li>Screwdriver, pliers, hammer, aviation snips</li>
          </ul>

          <h3 className="subhead"><span className="subhead-num">1.4</span>Safety</h3>

          <div className="warn">
            <div className="warn-label">Read before you cut anything</div>
            <p style={{ margin: 0 }}><strong>Aviation snips and hacksaw blades:</strong> the cut edges are sharp. Keep your fingers behind the cutting line. If a metal shard pops loose, it can fly — wear safety glasses if any are available.</p>
            <p style={{ margin: '8px 0 0' }}><strong>Hot wire:</strong> when current flows through the electromagnet coil, the wire warms up. Keep the circuit closed for no more than a few seconds while testing. If anything starts to feel hot, disconnect the battery.</p>
            <p style={{ margin: '8px 0 0' }}><strong>Battery short:</strong> never touch both battery terminals together with a bare metal object. It can drain the battery instantly and get hot enough to burn.</p>
          </div>
        </section>

        {/* SECTION 2: HOW IT WORKS */}
        <section id="electromagnet">
          <div className="section-eyebrow">Section Two</div>
          <div className="section-head">
            <span className="section-number">2.0</span>
            <h2>How an Electromagnet Works</h2>
          </div>

          <p className="lead">Before you start wrapping wire around a nail, take five minutes to understand what is about to happen.</p>

          <p>You already know what a regular magnet is. A bar magnet or a refrigerator magnet has two poles (north and south), and it can pull on iron and steel objects like paper clips or nails. That magnetism is built into the material itself. It is always on. You cannot turn a refrigerator magnet off, and you cannot make it stronger or weaker. It just <em>is</em>.</p>

          <p>An electromagnet is different. It is a magnet that you create using electricity, which means you can turn it on and off, and you can change how strong it is.</p>

          <h3 className="subhead"><span className="subhead-num">2.1</span>The key idea</h3>

          <p>Whenever electric current flows through a wire, it creates a small magnetic field around that wire. A single straight wire only produces a very weak field, far too weak to notice. But if you coil the wire into many loops, the magnetic fields from each loop stack together and combine into one much stronger field running through the middle of the coil. Wrap that coil around a piece of iron (like a steel nail), and the iron concentrates the field even more, turning the whole thing into a powerful magnet.</p>

          <p>An electromagnet has three ingredients working together:</p>

          <ol className="steps">
            <li><strong>A source of electricity</strong> — your two D batteries</li>
            <li><strong>A coil of wire</strong> — the more loops, the stronger the magnet</li>
            <li><strong>An iron or steel core</strong> — the nail, which focuses the magnetic field</li>
          </ol>

          <p>When you connect the wire to the battery, current flows, the coil produces a magnetic field, and the nail becomes magnetic. The moment you disconnect the battery, the current stops, the magnetic field disappears, and the nail goes back to being just a nail.</p>

          <h3 className="subhead"><span className="subhead-num">2.2</span>Side by side</h3>

          <table className="compare">
            <thead>
              <tr><th></th><th>Regular Magnet</th><th>Electromagnet</th></tr>
            </thead>
            <tbody>
              <tr><th>Always on?</th><td>Yes</td><td>Only when current is flowing</td></tr>
              <tr><th>Strength</th><td>Fixed</td><td>Depends on coils, current, and core</td></tr>
              <tr><th>Can you control it?</th><td>No</td><td>Yes, with a switch</td></tr>
              <tr><th>What makes it magnetic?</th><td>The material itself</td><td>Electric current in a coil</td></tr>
            </tbody>
          </table>

          <div className="tip">
            <strong>Why this matters</strong>
            The whole reason a telegraph works is because you can turn the magnet on and off very quickly by tapping a switch. Each tap pulses electricity through the coil, the nail becomes magnetic for an instant, and it pulls down a piece of springy metal that makes a click. Without the ability to switch a magnet on and off, there is no telegraph.
          </div>
        </section>

        {/* SECTION 3: STATION A */}
        <section id="station-a">
          <div className="section-eyebrow">Section Three</div>
          <div className="section-head">
            <span className="section-number">3.0</span>
            <h2>Station A — Electromagnet &amp; Sounder</h2>
          </div>

          <div className="station-banner">
            <div className="station-letter">A</div>
            <div>
              <h3>For the two students on this station</h3>
              <p>You will build the electromagnet and the sounder armature</p>
            </div>
          </div>

          <p>You are responsible for the two pieces that turn electricity into sound: the <strong>electromagnet</strong>, which becomes magnetic when current flows, and the <strong>sounder armature</strong>, a springy piece of metal that snaps down onto the nail with a sharp click. Get these two parts right and your telegraph has a voice.</p>

          <h3 className="subhead"><span className="subhead-num">3.1</span>Wrap the electromagnet coil</h3>

          <p>This is the heart of the telegraph. Take your time. A well-wrapped coil makes a stronger, more reliable magnet.</p>

          <p><strong>What you need for this step:</strong> the long steel nail, about 3 metres of 20-gauge wire, sandpaper.</p>

          <ol className="steps">
            <li><strong>Leave a tail.</strong> Start by leaving about 20 cm (8 inches) of wire loose at one end. You will need this tail later to connect the electromagnet to the rest of your circuit. Do not wrap this part around the nail.</li>
            <li><strong>Begin wrapping at the head of the nail.</strong> Hold the wire firmly against the nail just below the head. Start wrapping in tight, even loops. Wrap in the <em>same direction</em> the whole time — do not switch directions partway through, or the magnetic fields from your loops will cancel each other out instead of adding up.</li>
            <li><strong>Wrap three full layers.</strong> Wrap your way down the nail toward the point, keeping the loops tight and close together. When you reach near the tip, wrap back up toward the head for a second layer on top of the first. Then wrap down again for a third layer. Three layers gives you a strong, reliable magnet without using up all your wire.</li>
            <li><strong>Leave another tail.</strong> When you finish your third layer, leave another 20 cm of wire loose at the other end. Cut any extra wire.</li>
            <li><strong>Sand the wire ends.</strong> 20-gauge wire is coated with a thin layer of insulation that stops electricity from flowing. Use your sandpaper to gently sand about 2 cm of insulation off each of the two tails. The exposed copper should look shiny. <em>If you skip this step, no electricity will flow and your magnet will not work.</em></li>
          </ol>

          <figure>
            <svg className="fig-coil" width="100%" viewBox="0 0 680 360" role="img" xmlns="http://www.w3.org/2000/svg">
              <title>Wrapping wire around the nail</title>
              <desc>A line drawing showing a long nail with insulated wire wrapped in three layers around the shaft, with two long tails extending from each end.</desc>

              {/* Nail head */}
              <circle cx="80" cy="180" r="22" className="ink-line" strokeWidth="2" />
              <line x1="65" y1="170" x2="95" y2="170" className="ink-line" strokeWidth="1" />
              <line x1="65" y1="180" x2="95" y2="180" className="ink-line" strokeWidth="1" />
              <line x1="65" y1="190" x2="95" y2="190" className="ink-line" strokeWidth="1" />

              {/* Nail shaft */}
              <path d="M 100 165 L 560 175 L 590 180 L 560 185 L 100 195 Z" className="ink-line" strokeWidth="2" />

              {/* Left tail */}
              <path
                className="ink-line tail-left"
                strokeWidth="1.5"
                d="M 20 280 Q 60 260 100 220 Q 110 210 115 195"
              />

              {/* All coils */}
              {makeCoils()}

              {/* Right tail */}
              <path
                className="ink-line tail-right"
                strokeWidth="1.5"
                d="M 300 156 Q 340 130 400 100 Q 460 75 540 70"
              />

              {/* Labels */}
              <g className="fade-label" style={{ animationDelay: '7s' }}>
                <line x1="20" y1="280" x2="80" y2="320" className="leader-l" />
                <text x="120" y="318" textAnchor="middle" className="label-t">Tail 1 — sand this end</text>
                <text x="120" y="335" textAnchor="middle" className="label-m">~20 cm of free wire</text>
              </g>
              <g className="fade-label" style={{ animationDelay: '7.3s' }}>
                <line x1="540" y1="70" x2="540" y2="40" className="leader-l" />
                <text x="540" y="30" textAnchor="middle" className="label-t">Tail 2 — sand this end too</text>
              </g>
              <g className="fade-label" style={{ animationDelay: '7.6s' }}>
                <line x1="208" y1="220" x2="208" y2="248" className="leader-l" />
                <text x="320" y="268" textAnchor="middle" className="label-t">Three layers, tight loops</text>
                <text x="320" y="285" textAnchor="middle" className="label-m">all wrapped the same direction</text>
              </g>
              <g className="fade-label" style={{ animationDelay: '7.9s' }}>
                <line x1="80" y1="158" x2="80" y2="120" className="leader-l" />
                <text x="80" y="110" textAnchor="middle" className="label-t">Head</text>
              </g>
              <g className="fade-label" style={{ animationDelay: '8s' }}>
                <line x1="585" y1="195" x2="585" y2="230" className="leader-l" />
                <text x="585" y="250" textAnchor="middle" className="label-t">Point</text>
              </g>
            </svg>
            <figcaption><span className="fig-num">Figure 3.1</span>Wrap three layers tightly down, up, then down the nail. Watch the diagram animate — the order matters.</figcaption>
          </figure>

          <h3 className="subhead"><span className="subhead-num">3.2</span>Test before you mount</h3>

          <p>Do not screw anything to the wood yet. First, prove that your magnet works.</p>

          <ol className="steps">
            <li><strong>Connect both wire tails</strong> to a D battery using alligator clips — one tail to each terminal.</li>
            <li><strong>Hold the nail near small steel objects</strong> like a paper clip, a thumbtack, or a piece of metal tape. They should jump to the nail.</li>
            <li><strong>Disconnect one clip.</strong> The objects should fall away immediately.</li>
          </ol>

          <div className="warn">
            <div className="warn-label">If nothing happens</div>
            <p style={{ margin: 0 }}>Check three things in this order: <strong>(1)</strong> Are both wire ends sanded down to shiny copper? <strong>(2)</strong> Are the alligator clips making firm contact with the battery? <strong>(3)</strong> Are all your coils wrapped in the same direction?</p>
            <p style={{ margin: '8px 0 0' }}>Do not leave the magnet connected for more than a few seconds while testing. The wire warms up.</p>
          </div>

          <h3 className="subhead"><span className="subhead-num">3.3</span>Mount the electromagnet on the 2x4</h3>

          <p>The electromagnet sits at one end of the board, mounted <strong>vertically with the point of the nail pointing up</strong>. The sounder will hover just above the tip.</p>

          <ol className="steps">
            <li>Decide which end of your 2x4 will be the "electromagnet end." Roughly 5 cm in from that end, mark where the nail will sit.</li>
            <li>Cut a strip of metal tape about 6 cm long. Fold it lengthwise over the nail to make a strap.</li>
            <li>Position the nail vertically, point up. Use the metal-tape strap to hold the nail against the wood, with the ends of the strap going down to the board on either side of the nail.</li>
            <li>Screw the strap ends down to the wood with a small screw on each side. The nail should now stand up firmly, point in the air.</li>
            <li>The two wire tails should hang free — you will connect them when wiring the circuit later.</li>
          </ol>

          <h3 className="subhead"><span className="subhead-num">3.4</span>Build the sounder armature</h3>

          <p>The sounder is the part that makes the satisfying <em>click</em> when the magnet activates. It is a curved piece of springy steel mounted so its free end hovers just above the nail tip. When the magnet turns on, it pulls the steel down. When the magnet turns off, the steel's springiness pulls it back up.</p>

          <ol className="steps">
            <li><strong>Cut the longer piece</strong> of hacksaw blade with the aviation snips. You want a piece roughly 12-15 cm long. Hacksaw blades are made of hard steel — push slowly and keep your fingers behind the cut.</li>
            <li><strong>Sand the paint off one end.</strong> The blade is painted, which insulates it. About 2 cm of the end that will be mounted should be sanded to bare metal so the alligator clip can make electrical contact.</li>
            <li><strong>Bend the blade into a gentle arch.</strong> Use the pliers to curve it so one end can be screwed flat to the wood while the other end arches up and over the electromagnet's nail tip.</li>
            <li><strong>Mount the sanded end</strong> to the 2x4 with a screw, positioned so the arched free end sits directly above the nail tip with a gap of about <strong>2-3 mm</strong>. Too close and the blade will rest on the nail all the time. Too far and the magnet will not be strong enough to pull it down.</li>
          </ol>

          <div className="tip">
            <strong>Test the gap</strong>
            Once the sounder is mounted, briefly touch your battery clips to the electromagnet wires. The free end of the sounder should snap down onto the nail with a click. If it does not move, the gap is too wide — loosen the mounting screw and bend the blade slightly closer.
          </div>
        </section>

        {/* SECTION 4: STATION B */}
        <section id="station-b">
          <div className="section-eyebrow">Section Four</div>
          <div className="section-head">
            <span className="section-number">4.0</span>
            <h2>Station B — Power Supply &amp; Key</h2>
          </div>

          <div className="station-banner">
            <div className="station-letter">B</div>
            <div>
              <h3>For the two students on this station</h3>
              <p>You will build the battery holder, the telegraph key, and mount the light bulb</p>
            </div>
          </div>

          <p>You are responsible for the parts that supply and control the electricity: the <strong>battery pack</strong>, which provides power, the <strong>telegraph key</strong>, which is the switch students press to send signals, and the <strong>light bulb</strong>, which flashes every time the key is pressed as a visible signal indicator.</p>

          <h3 className="subhead"><span className="subhead-num">4.1</span>Mount the battery brackets</h3>

          <p>Your two D batteries connect <strong>in series</strong>, end to end, so their voltages add up to 3 volts. The angle brackets do double duty: they hold the batteries in place AND act as electrical contacts that the alligator clips can grip onto.</p>

          <ol className="steps">
            <li><strong>Decide which end of your 2x4 will hold the batteries.</strong> This should be the <em>opposite</em> end from where Station A is mounting the electromagnet. Leave plenty of space in the middle for the key and bulb.</li>
            <li><strong>Place the two batteries end to end</strong> on the wood, with the positive end of one touching the negative end of the other. Mark where the outer ends of the battery pack will sit.</li>
            <li><strong>Mount the first angle bracket</strong> at one end so it stands up vertically. The flat face of the bracket should press against the outer terminal of the first battery.</li>
            <li><strong>Place the small spring</strong> between the second bracket and the outer terminal of the second battery. The spring keeps tension on the batteries so they do not lose contact when the board is jostled.</li>
            <li><strong>Mount the second angle bracket</strong> with the spring compressed slightly against the battery terminal. Test that you can lift the batteries out and replace them.</li>
          </ol>

          <h3 className="subhead"><span className="subhead-num">4.2</span>Test the battery pack</h3>

          <p>Before going further, prove that your battery pack delivers power:</p>

          <ol className="steps">
            <li>Clip one alligator lead to each angle bracket.</li>
            <li>Touch the other ends of the leads to the two terminals of the light bulb.</li>
            <li>The bulb should light. If not, check that the batteries are touching each other firmly, the spring is making contact, and you are clipping onto the metal of the bracket (not the wood).</li>
          </ol>

          <h3 className="subhead"><span className="subhead-num">4.3</span>Mount the light bulb socket</h3>

          <p>The light bulb sits roughly in the middle of the 2x4, between the battery pack and the spot where the key will go. It is wired in series with the rest of the circuit, so it lights every time the key is pressed.</p>

          <ol className="steps">
            <li>Find a spot about a third of the way along the 2x4 from the battery end.</li>
            <li>Screw the light bulb socket directly to the wood through its mounting holes.</li>
            <li>Make sure the two terminal screws on the socket are accessible — you will clip alligator leads onto them later.</li>
          </ol>

          <h3 className="subhead"><span className="subhead-num">4.4</span>Build the telegraph key</h3>

          <p>The key is the switch you press to send signals. This is the most clever part of the whole build — and the trick is the <strong>fulcrum screw</strong> underneath the blade.</p>

          <p>You will mount a short piece of hacksaw blade flat on the wood, anchored at one end. A second screw underneath the blade — placed about 1.5 cm from the anchor — pushes the blade up slightly, holding it in a sprung position. A third screw sits under the free end, and that is the <strong>contact screw</strong>. Press the free end down, the blade touches the contact screw, the circuit closes. Release, and the blade springs back up on its own.</p>

          <div className="tip">
            <strong>Why no spring?</strong>
            The hacksaw blade IS the spring. Hardened steel is naturally springy. Bend it slightly with the fulcrum screw and it pushes back. This is a classic telegraph design — Samuel Morse's original key used the same principle.
          </div>

          <ol className="steps">
            <li><strong>Cut a short piece of hacksaw blade,</strong> about 6-8 cm long, using the aviation snips. This is the shorter of the two pieces you will cut from the blade. Keep your fingers clear.</li>
            <li><strong>Sand the paint off the top surface</strong> of the blade along most of its length. This exposes the metal so the alligator clip and the contact screw can make electrical contact.</li>
            <li><strong>Place a strip of metal tape</strong> on the sanded surface for extra conductivity if needed.</li>
            <li><strong>Mount the anchor end.</strong> Drive a screw through one end of the blade into the wood. The blade should now stick out flat, free at the other end.</li>
            <li><strong>Place the fulcrum screw.</strong> Drive a second screw into the wood <em>directly underneath</em> the blade, about <strong>1.5 cm in front</strong> of the anchor screw. Tighten it until its head pushes the blade up slightly — like a lever pivoting on the screw head. The free end of the blade should now rest a few millimetres above the wood.</li>
            <li><strong>Place the contact screw.</strong> Drive a third screw into the wood near the free end of the blade, in a position where pressing the blade down will bring it firmly onto the screw head. This screw is the contact point that closes the circuit.</li>
          </ol>

          <figure>
            <svg width="100%" viewBox="0 0 680 280" role="img" xmlns="http://www.w3.org/2000/svg">
              <title>Side view of the telegraph key with fulcrum-screw mechanism</title>
              <desc>A diagram showing the sprung-key mechanism: a hacksaw blade anchored at one end, lifted by a fulcrum screw, with a contact screw beneath the free end.</desc>

              {/* Wood */}
              <rect x="60" y="180" width="560" height="60" className="key-wood" />
              <path d="M 60 195 Q 200 192, 340 195 T 620 195" fill="none" stroke="#8B6F47" strokeWidth="0.4" opacity="0.4" />
              <path d="M 60 215 Q 200 218, 340 215 T 620 215" fill="none" stroke="#8B6F47" strokeWidth="0.4" opacity="0.4" />
              <path d="M 60 230 Q 200 227, 340 230 T 620 230" fill="none" stroke="#8B6F47" strokeWidth="0.4" opacity="0.4" />

              {/* Blade */}
              <polygon points="130,170 480,150 480,158 130,178" className="key-blade" />

              {/* Anchor screw */}
              <circle cx="145" cy="174" r="6" className="key-screw" />
              <line x1="141" y1="174" x2="149" y2="174" stroke="#233C5F" strokeWidth="1" />

              {/* Fulcrum screw */}
              <rect x="207" y="170" width="10" height="14" fill="#A29164" stroke="#233C5F" strokeWidth="1" />
              <circle cx="212" cy="167" r="6" className="key-screw" />
              <line x1="208" y1="167" x2="216" y2="167" stroke="#233C5F" strokeWidth="1" />

              {/* Contact screw */}
              <rect x="465" y="160" width="10" height="22" fill="#A29164" stroke="#233C5F" strokeWidth="1" />
              <circle cx="470" cy="157" r="6" className="key-screw" />
              <line x1="466" y1="157" x2="474" y2="157" stroke="#233C5F" strokeWidth="1" />

              {/* Gap indicator */}
              <line x1="480" y1="158" x2="480" y2="153" stroke="#E83530" strokeWidth="1" strokeDasharray="2 2" />
              <line x1="464" y1="153" x2="488" y2="153" stroke="#E83530" strokeWidth="0.8" strokeDasharray="2 2" />
              <text x="494" y="156" className="key-it">gap</text>

              {/* Press arrow */}
              <defs>
                <marker id="tg-arr1" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                  <path d="M2 1L8 5L2 9" fill="none" stroke="#E83530" strokeWidth="2" strokeLinecap="round" />
                </marker>
              </defs>
              <path d="M 460 100 L 460 145" stroke="#E83530" strokeWidth="2" fill="none" markerEnd="url(#tg-arr1)" />
              <text x="475" y="120" className="key-label">PRESS</text>

              {/* Labels */}
              <line x1="145" y1="174" x2="145" y2="50" className="key-leader" />
              <text x="145" y="42" textAnchor="middle" className="key-label">Anchor screw</text>
              <text x="145" y="58" textAnchor="middle" className="key-it">blade pivots here</text>

              <line x1="212" y1="167" x2="212" y2="75" className="key-leader" />
              <text x="212" y="67" textAnchor="middle" className="key-label">Fulcrum screw</text>
              <text x="212" y="83" textAnchor="middle" className="key-it">props blade up</text>

              <line x1="470" y1="157" x2="565" y2="200" className="key-leader" />
              <text x="615" y="200" textAnchor="middle" className="key-label">Contact screw</text>
              <text x="615" y="217" textAnchor="middle" className="key-it">closes the circuit</text>
            </svg>
            <figcaption><span className="fig-num">Figure 4.1</span>Side view of the key (the red bar is the hacksaw blade, sanded surface facing up). The fulcrum screw under the blade props it up so the free end rests slightly above the contact screw. Pressing closes the gap. Release lets the blade's natural springiness push it back up.</figcaption>
          </figure>

          <h3 className="subhead"><span className="subhead-num">4.5</span>Test the key</h3>

          <ol className="steps">
            <li>Clip one alligator lead to the anchor screw (this is one side of your switch).</li>
            <li>Clip the other lead to the contact screw underneath (this is the other side).</li>
            <li>Connect those two leads through the light bulb and the battery pack to form a simple circuit.</li>
            <li>Press the blade. The bulb should light when pressed and go dark when released. If it stays on or stays off, check the fulcrum-screw height.</li>
          </ol>
        </section>

        {/* SECTION 5: WIRING */}
        <section id="wiring">
          <div className="section-eyebrow">Section Five</div>
          <div className="section-head">
            <span className="section-number">5.0</span>
            <h2>Wire It All Together</h2>
          </div>

          <p className="lead">All four of you, working as one team. Both pairs bring their finished components to the bench and combine them into a single working circuit.</p>

          <p>The whole circuit is a single loop. Electricity leaves the battery's positive terminal, travels through the key, through the light bulb, through the electromagnet coil, and back to the battery's negative terminal. When the key is open, the loop is broken and nothing flows. When the key is pressed, the loop closes, electricity flows, the bulb lights, and the electromagnet pulls the sounder down with a click.</p>

          <h3 className="subhead"><span className="subhead-num">5.1</span>The circuit, in order</h3>

          <figure>
            <svg width="100%" viewBox="0 0 680 280" role="img" xmlns="http://www.w3.org/2000/svg">
              <title>Circuit diagram of the telegraph</title>
              <desc>A schematic showing battery, telegraph key, light bulb, and electromagnet wired in a single series loop.</desc>

              {/* Battery */}
              <line x1="80" y1="60" x2="80" y2="100" stroke="#233C5F" strokeWidth="4" />
              <line x1="100" y1="50" x2="100" y2="110" stroke="#233C5F" strokeWidth="2" />
              <line x1="100" y1="160" x2="100" y2="200" stroke="#233C5F" strokeWidth="2" />
              <line x1="80" y1="170" x2="80" y2="190" stroke="#233C5F" strokeWidth="4" />
              <text x="60" y="135" className="c-text" textAnchor="middle">Battery</text>
              <text x="60" y="151" className="c-text" textAnchor="middle" style={{ fontSize: '11px' }}>3V</text>
              <text x="115" y="84" className="c-text" style={{ fontSize: '12px' }}>+</text>
              <text x="115" y="186" className="c-text" style={{ fontSize: '12px' }}>−</text>

              <path d="M 100 80 L 220 80" className="c-wire circuit-current" />
              <text x="160" y="68" className="c-num" textAnchor="middle">1</text>

              {/* Key */}
              <circle cx="225" cy="80" r="3" fill="#233C5F" />
              <line x1="225" y1="80" x2="255" y2="65" stroke="#233C5F" strokeWidth="2" />
              <circle cx="265" cy="80" r="3" fill="#233C5F" />
              <text x="245" y="100" className="c-text" textAnchor="middle">Key</text>

              <path d="M 265 80 L 360 80" className="c-wire circuit-current" style={{ animationDelay: '-0.3s' }} />
              <text x="312" y="68" className="c-num" textAnchor="middle">2</text>

              {/* Bulb */}
              <circle cx="400" cy="80" r="20" className="c-comp" />
              <line x1="386" y1="66" x2="414" y2="94" stroke="#233C5F" strokeWidth="1" />
              <line x1="386" y1="94" x2="414" y2="66" stroke="#233C5F" strokeWidth="1" />
              <text x="400" y="125" className="c-text" textAnchor="middle">Bulb</text>

              <path d="M 420 80 L 540 80" className="c-wire circuit-current" style={{ animationDelay: '-0.6s' }} />
              <text x="480" y="68" className="c-num" textAnchor="middle">3</text>

              {/* Electromagnet */}
              <rect x="555" y="60" width="40" height="40" fill="none" stroke="#233C5F" strokeWidth="1.5" />
              <path d="M 560 65 Q 565 75 560 85 Q 565 95 560 100" stroke="#233C5F" strokeWidth="2" fill="none" />
              <path d="M 570 65 Q 575 75 570 85 Q 575 95 570 100" stroke="#233C5F" strokeWidth="2" fill="none" />
              <path d="M 580 65 Q 585 75 580 85 Q 585 95 580 100" stroke="#233C5F" strokeWidth="2" fill="none" />
              <line x1="590" y1="60" x2="590" y2="100" stroke="#233C5F" strokeWidth="3" />
              <text x="575" y="125" className="c-text" textAnchor="middle">Electromagnet</text>

              <path d="M 575 100 L 575 180 L 100 180" className="c-wire circuit-current" style={{ animationDelay: '-0.9s' }} />
              <text x="350" y="200" className="c-num" textAnchor="middle">4</text>

              <text x="340" y="245" className="c-text" textAnchor="middle" style={{ fontSize: '13px' }}>Current flows in a single loop when the key is pressed</text>
              <text x="340" y="263" textAnchor="middle" style={{ fill: '#E83530', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: '14px' }}>red arrows show the direction of flow</text>
            </svg>
            <figcaption><span className="fig-num">Figure 5.1</span>The complete circuit. Battery → key → light bulb → electromagnet → back to battery. One closed loop, four connections.</figcaption>
          </figure>

          <h3 className="subhead"><span className="subhead-num">5.2</span>Make the four connections</h3>

          <p>Use alligator clip leads for every connection. They are easy to attach, easy to swap, and easy to debug.</p>

          <ol className="steps">
            <li><strong>Connection 1 — Battery (+) to Key:</strong> clip one end of a lead onto the battery angle bracket at the positive end. Clip the other end onto the <em>anchor screw</em> of the telegraph key.</li>
            <li><strong>Connection 2 — Key to Light bulb:</strong> clip one end onto the <em>contact screw</em> of the key (the screw under the free end of the blade). Clip the other end onto one terminal of the light bulb socket.</li>
            <li><strong>Connection 3 — Light bulb to Electromagnet:</strong> clip one end onto the other terminal of the light bulb socket. Clip the other end onto one of the sanded wire tails coming from the electromagnet.</li>
            <li><strong>Connection 4 — Electromagnet to Battery (−):</strong> clip one end onto the other sanded wire tail from the electromagnet. Clip the other end onto the battery angle bracket at the negative end.</li>
          </ol>

          <h3 className="subhead"><span className="subhead-num">5.3</span>The first test</h3>

          <p>Take a breath. Press the key.</p>

          <p>If everything is right, three things happen at once:</p>

          <ul className="result-list">
            <li><span className="result-mark">✦</span>The light bulb flashes</li>
            <li><span className="result-mark">✦</span>The sounder snaps down with a click</li>
            <li><span className="result-mark">✦</span>You can hear the click from across the room</li>
          </ul>

          <h3 className="subhead"><span className="subhead-num">5.4</span>If it does not work</h3>

          <p>Do not panic and do not start unbuilding things. Work through this list in order. Most telegraphs that "do not work" have one of these five problems.</p>

          <div className="card">
            <span className="card-flag">Troubleshooting</span>
            <table className="compare" style={{ margin: 0 }}>
              <thead><tr><th>Symptom</th><th>Likely cause</th></tr></thead>
              <tbody>
                <tr><th>Bulb does not light at all</th><td>A wire tail still has insulation on it. Re-sand both ends until they shine.</td></tr>
                <tr><th>Bulb stays lit even without pressing</th><td>The fulcrum screw is too low — the blade is resting on the contact screw. Tighten the fulcrum screw to lift the blade higher.</td></tr>
                <tr><th>Bulb lights but no click</th><td>The sounder gap is too wide. Bend the sounder blade slightly closer to the nail tip (2-3 mm gap).</td></tr>
                <tr><th>Click but no light</th><td>The light bulb is burned out or the socket connection is loose. Reseat the bulb.</td></tr>
                <tr><th>Nothing at all</th><td>The batteries are dead, installed backwards, or not making contact. Reset them with the spring.</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* SECTION 6: MORSE */}
        <section id="morse">
          <div className="section-eyebrow">Section Six</div>
          <div className="section-head">
            <span className="section-number">6.0</span>
            <h2>Sending Messages in Morse Code</h2>
          </div>

          <p className="lead">Your telegraph works. Now make it say something.</p>

          <p>Morse code uses two signals — a short tap (<strong>dot</strong>) and a long tap (<strong>dash</strong>) — to spell out every letter and number. A dash should last about three times longer than a dot. Leave a brief pause between letters and a longer pause between words.</p>

          <h3 className="subhead"><span className="subhead-num">6.1</span>The alphabet</h3>

          <div className="morse-grid">
            {morseAlphabet.map(([letter, code]) => (
              <div className="morse-cell" key={letter}>
                <span className="morse-letter">{letter}</span>
                <span className="morse-code">{code}</span>
              </div>
            ))}
          </div>

          <h3 className="subhead"><span className="subhead-num">6.2</span>Start with the simple ones</h3>

          <p>Try tapping these out before attempting full words. Time yourself.</p>

          <div className="card">
            <span className="card-flag gold">Practice list</span>
            <table className="practice-table">
              <tbody>
                <tr><td className="pw">SOS</td><td className="pc">··· −−− ···</td><td className="pn">the universal distress signal</td></tr>
                <tr><td className="pw">HI</td><td className="pc">···· ··</td><td className="pn">all dots</td></tr>
                <tr><td className="pw">OK</td><td className="pc">−−− −·−</td><td className="pn">three dashes, then dash-dot-dash</td></tr>
                <tr><td className="pw">YES</td><td className="pc">−·−− · ···</td><td className="pn">three letters</td></tr>
                <tr><td className="pw">KING</td><td className="pc">−·− ·· −· −−·</td><td className="pn">four letters</td></tr>
              </tbody>
            </table>
          </div>

          <h3 className="subhead"><span className="subhead-num">6.3</span>Send a message to another group</h3>

          <p>Pair up with the group at the next bench. One group is the <strong>sender</strong>, the other is the <strong>receiver</strong>. The sender taps out a word in Morse. The receiver writes down the dots and dashes they hear, then decodes them using the chart above. Then switch roles.</p>

          <div className="tip">
            <strong>Challenge</strong>
            Can the receiver translate a three-letter word without looking at the chart? Can the sender send "HELP" fast enough that the receiver can keep up? Try increasing speed gradually — the real telegraph operators of the 1800s could send 30+ words a minute.
          </div>

          <h3 className="subhead"><span className="subhead-num">6.4</span>Reflection questions</h3>

          <p>Before you pack up, talk through these with your group:</p>

          <ol className="steps">
            <li>What would happen to your telegraph if you wrapped only one layer of wire around the nail instead of three? What about ten layers?</li>
            <li>Why does the sounder blade need to be made of <em>iron-bearing</em> steel? What if it were aluminum or copper?</li>
            <li>If you wanted to send a message to a friend in the next classroom, what would you have to change about your telegraph? What would stay the same?</li>
            <li>The telegraph let people send messages across continents in seconds. Before the telegraph, the fastest message could move only as fast as a horse or a ship. How do you think this changed the world?</li>
          </ol>
        </section>

      </main>

      {/* ====== FOOTER ====== */}
      <footer className="tg-footer">
        <svg className="footer-ornament" viewBox="0 0 100 100" aria-hidden="true">
          <g opacity="0.8">
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
              <path
                key={deg}
                d="M50 12 L56 40 L50 36 L44 40 Z"
                fill="#C7B485"
                transform={`rotate(${deg} 50 50)`}
              />
            ))}
          </g>
          <circle cx="50" cy="50" r="10" fill="#C7B485" />
        </svg>
        <p className="motto">Floreat Scientia</p>
        <p>Designed for grade 8 science at King's Academy · Madaba, Jordan</p>
      </footer>
    </div>
  );
}